/**
 * Where a candidate sat in the confirm-screen list, and how much of that list the
 * office was looking at.
 *
 * Shared by every `confirm_candidate` event because the whole point is joining an
 * impression to its outcome: if `candidate_viewed` and `confirm_clicked` derived
 * or spelled these properties differently, the two couldn't be compared per
 * candidate (GOTU-4816 / GOTU-4788). Populate it from
 * `getCandidatePosition()` in `~/components/ConfirmCandidate/utils` — never by
 * hand at a call site.
 */
export interface IConfirmCandidatePositionContext {
  shiftId: string;
  professionalId?: string;
  offerId?: string;
  /** 1-indexed and absolute across pages. Undefined when the candidate isn't in the list. */
  candidateNumber?: number;
  candidateCount?: number;
  /** Which page of the list `candidateNumber` falls on, at the list's current limit. */
  candidatePage?: number;
  /** Rows rendered on `candidatePage`; smaller than the limit on a partial last page. */
  visibleCandidateCount?: number;
}

/**
 * Maps the position context to its snake_case PostHog properties.
 *
 * One mapping for all six events, so a property can't drift to a different name
 * in one of them and quietly break the join.
 */
export const toCandidatePositionProperties = (
  payload: IConfirmCandidatePositionContext,
): Record<string, unknown> => ({
  shift_id: payload.shiftId,
  professional_id: payload.professionalId,
  offer_id: payload.offerId,
  candidate_number: payload.candidateNumber,
  candidate_count: payload.candidateCount,
  candidate_page: payload.candidatePage,
  visible_candidate_count: payload.visibleCandidateCount,
});
